import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Row, Col, Collapse, Input, FormGroup, InputGroup } from 'reactstrap';
import { PinkButtonContainer, EditIcon, RotatingIcon, Colors, GreyLabel, HighlightedInputGroupText } from '../../../utils/budget-tool-specific';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FlightAPIService } from './services/FlightAPIService';
import { selectBestFlights, calculatePriceAverage, calculatePriceMin, calculatePriceMax, calculateDurationAverage, calculateDurationMin, calculateStopsAverage, calculateDurationMax, calculateStopsMin, calculateStopsMax } from '../flights/flight-helper';

const FlightExperimentalInputRow = (props) => {
    const [isFetching, setIsFetching] = useState(false);
    const [flightData, setFlightData] = useState({
        priceAverage: 0,
        priceMin: 0,
        priceMax: 0,
        durationAverage: 0,
        durationMin: 0,
        durationMax: 0,
        stopsAverage: 0,
        stopsMin: 0,
        stopsMax: 0,
        originAirport: "",
        numPeople: 1,
        id: props.flight.id || `flight_${Date.now()}`
    });

    const [isOpen, setIsOpen] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [flightService] = useState(() => new FlightAPIService(props.provider || 'flightapi'));

    useEffect(() => {
        if (props.flight) {
            setFlightData(prev => ({
                ...prev,
                ...props.flight
            }));
        }
    }, [props.flight]);

    useEffect(() => {
        flightService.setProvider(props.provider || 'flightapi');
    }, [props.provider]);

    const handleInputChange = (field, value) => {
        const newFlightData = { ...flightData };

        if (field === 'numPeople') {
            // Cap at 9 as mentioned in the requirements
            value = Math.min(Math.max(1, parseInt(value) || 1), 9);
        }

        newFlightData[field] = value;
        setFlightData(newFlightData);

        // Update the flight in parent component
        if (props.updateFlight) {
            props.updateFlight(props.index, newFlightData);
        }
    };

    const setFlightDetails = (resp) => {
        setIsFetching(false);
        setSearchError(null);

        try {
            let bFlights = selectBestFlights(resp, 5, flightData.numPeople);
            let newFlights = {};
            newFlights.priceAverage = calculatePriceAverage(bFlights);
            newFlights.priceMin = calculatePriceMin(bFlights);
            newFlights.priceMax = calculatePriceMax(bFlights);
            newFlights.durationAverage = calculateDurationAverage(bFlights, resp.data.segments);
            newFlights.durationMin = calculateDurationMin(bFlights, resp.data.segments);
            newFlights.durationMax = calculateDurationMax(bFlights, resp.data.segments);
            newFlights.stopsAverage = calculateStopsAverage(bFlights);
            newFlights.stopsMin = calculateStopsMin(bFlights);
            newFlights.stopsMax = calculateStopsMax(bFlights);

            const updatedFlightData = {
                ...flightData,
                ...newFlights
            };

            setFlightData(updatedFlightData);

            // Update parent
            if (props.updateFlight) {
                props.updateFlight(props.index, updatedFlightData);
            }

            console.log(`Flight search completed for ${flightData.originAirport} with ${props.provider}:`, {
                priceAverage: newFlights.priceAverage,
                provider: props.provider
            });

        } catch (error) {
            console.error('Error processing flight results:', error);
            setSearchError('Error processing flight results');
        }
    };

    const setError = (err) => {
        console.error('Flight search error:', err);
        setIsFetching(false);
        setSearchError(err.message || 'Flight search failed. Please try again.');
    };

    const fetchFlight = async () => {
        if (!flightData.originAirport || flightData.originAirport.length !== 3) {
            setSearchError("Please enter a valid 3-letter origin airport code");
            return;
        }

        if (!props.data.destination.value || !props.data.departureDate.value || !props.data.returnDate.value) {
            setSearchError("Please fill in destination, departure date, and return date first");
            return;
        }

        setIsFetching(true);
        setSearchError(null);

        try {
            const searchParams = {
                origin: flightData.originAirport.toUpperCase(),
                destination: props.data.destination.value.toUpperCase(),
                departureDate: props.data.departureDate.value,
                returnDate: props.data.returnDate.value,
                adults: flightData.numPeople,
                currency: props.data.currency.value
            };

            console.log(`Searching flight with ${props.provider}:`, searchParams);

            const results = await flightService.searchFlights(searchParams);
            setFlightDetails(results);

        } catch (error) {
            console.error('Flight search failed:', error);
            setError(error);
        }
    };

    const displayMinutes = (value) => {
        if (!value) return "0 min";
        let text = "";
        let minutes = value % 60;
        let hours = Math.floor(value / 60);
        if (hours !== 0) {
            text += hours + " hr ";
        }
        text += minutes + " min";
        return text;
    };

    const displayDollars = (value) => {
        if (!value) return "0";
        return value.toLocaleString("en-US");
    };

    const normalStyle = {
        transition: '1s, transform 0.5s',
    };

    const transformedStyle = {
        transition: '1s, transform 0.5s',
        transform: 'rotate(180deg)',
    };

    return (
        <>
            <Row style={{ marginBottom: "10px", backgroundColor: searchError ? "#f8d7da" : "transparent", padding: "10px", borderRadius: "8px" }}>
                {!props.client && (
                    <Col lg="auto" style={{ paddingTop: "30px", paddingLeft: "15px", paddingRight: "0px" }}>
                        <PinkButtonContainer onClick={fetchFlight} disabled={isFetching}>
                            <RotatingIcon isOn={isFetching} icon={['fas', 'sync']} style={{ height: "25px" }} />
                        </PinkButtonContainer>
                    </Col>
                )}
                <Col lg="auto" style={{ paddingTop: "35px", paddingLeft: "15px", paddingRight: "0px" }}>
                    <ButtonContainer onClick={() => setIsOpen(!isOpen)}>
                        <CollapseIcon icon={['fas', 'chevron-down']} style={isOpen ? transformedStyle : normalStyle} />
                    </ButtonContainer>
                </Col>
                <Col>
                    <Row>
                        <Col lg="1">
                            <FormGroup>
                                <GreyLabel>Origin</GreyLabel>
                                <InputGroup>
                                    <HighlightedInput
                                        type="text"
                                        value={flightData.originAirport}
                                        onChange={(e) => handleInputChange('originAirport', e.target.value.toUpperCase())}
                                        maxLength={3}
                                        placeholder="YYZ"
                                        readOnly={props.client}
                                    />
                                </InputGroup>
                            </FormGroup>
                        </Col>
                        <Col lg="1">
                            <FormGroup>
                                <GreyLabel>Pass.</GreyLabel>
                                <InputGroup>
                                    <HighlightedInput
                                        type="number"
                                        value={flightData.numPeople}
                                        onChange={(e) => handleInputChange('numPeople', e.target.value)}
                                        min={1}
                                        max={9}
                                        readOnly={props.client}
                                    />
                                </InputGroup>
                            </FormGroup>
                        </Col>
                        <Col lg="2">
                            <FormGroup>
                                <GreyLabel>Price Min</GreyLabel>
                                <InputGroup>
                                    <HighlightedInputGroupText style={{ backgroundColor: "#f7e7bc", border: "none" }}>
                                        {props.data.currency.value.slice(3)}
                                    </HighlightedInputGroupText>
                                    <UnHighlightedInput
                                        style={{ backgroundColor: "#f7e7bc" }}
                                        value={displayDollars(flightData.priceMin)}
                                        readOnly
                                    />
                                </InputGroup>
                            </FormGroup>
                        </Col>
                        <Col lg="2">
                            <FormGroup>
                                <GreyLabel>Price Average</GreyLabel>
                                <InputGroup>
                                    <HighlightedInputGroupText style={{ backgroundColor: "#f7e7bc", border: "none" }}>
                                        {props.data.currency.value.slice(3)}
                                    </HighlightedInputGroupText>
                                    <UnHighlightedInput
                                        style={{ backgroundColor: "#f7e7bc" }}
                                        value={displayDollars(flightData.priceAverage)}
                                        readOnly
                                    />
                                </InputGroup>
                            </FormGroup>
                        </Col>
                        <Col lg="2">
                            <FormGroup>
                                <GreyLabel>Average Duration</GreyLabel>
                                <InputGroup>
                                    <UnHighlightedInput
                                        style={{ backgroundColor: "#f7e7bc" }}
                                        value={displayMinutes(flightData.durationAverage)}
                                        readOnly
                                    />
                                </InputGroup>
                            </FormGroup>
                        </Col>
                        <Col lg="2">
                            <FormGroup>
                                <GreyLabel>Avg Connections</GreyLabel>
                                <InputGroup>
                                    <UnHighlightedInput
                                        style={{ backgroundColor: "#f7e7bc" }}
                                        value={flightData.stopsAverage || 0}
                                        readOnly
                                    />
                                </InputGroup>
                            </FormGroup>
                        </Col>
                        <Col lg="2">
                            <FormGroup>
                                <GreyLabel>Price Avg. Total</GreyLabel>
                                <InputGroup>
                                    <HighlightedInputGroupText style={{ backgroundColor: "#f7e7bc", border: "none" }}>
                                        {props.data.currency.value.slice(3)}
                                    </HighlightedInputGroupText>
                                    <UnHighlightedInput
                                        style={{ backgroundColor: "#f7e7bc" }}
                                        value={displayDollars(flightData.priceAverage * flightData.numPeople)}
                                        readOnly
                                    />
                                </InputGroup>
                            </FormGroup>
                        </Col>
                    </Row>

                    {/* Error Display */}
                    {searchError && (
                        <Row>
                            <Col>
                                <div style={{
                                    color: "#721c24",
                                    backgroundColor: "#f8d7da",
                                    border: "1px solid #f5c6cb",
                                    borderRadius: "6px",
                                    padding: "8px 12px",
                                    fontSize: "12px",
                                    marginTop: "5px"
                                }}>
                                    ❌ {searchError}
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Expanded Details */}
                    <Collapse isOpen={isOpen}>
                        <Row style={{ marginTop: "10px" }}>
                            <Col lg="2">
                                <FormGroup>
                                    <GreyLabel>Duration Max</GreyLabel>
                                    <InputGroup>
                                        <UnHighlightedInput
                                            style={{ backgroundColor: "#f7e7bc" }}
                                            value={displayMinutes(flightData.durationMax)}
                                            readOnly
                                        />
                                    </InputGroup>
                                </FormGroup>
                            </Col>
                            <Col lg="2">
                                <FormGroup>
                                    <GreyLabel>Duration Min</GreyLabel>
                                    <InputGroup>
                                        <UnHighlightedInput
                                            style={{ backgroundColor: "#f7e7bc" }}
                                            value={displayMinutes(flightData.durationMin)}
                                            readOnly
                                        />
                                    </InputGroup>
                                </FormGroup>
                            </Col>
                            <Col lg="2">
                                <FormGroup>
                                    <GreyLabel>Price Max</GreyLabel>
                                    <InputGroup>
                                        <HighlightedInputGroupText style={{ backgroundColor: "#f7e7bc", border: "none" }}>
                                            {props.data.currency.value.slice(3)}
                                        </HighlightedInputGroupText>
                                        <UnHighlightedInput
                                            style={{ backgroundColor: "#f7e7bc" }}
                                            value={displayDollars(flightData.priceMax)}
                                            readOnly
                                        />
                                    </InputGroup>
                                </FormGroup>
                            </Col>
                            <Col lg="2">
                                <FormGroup>
                                    <GreyLabel>Stops Min</GreyLabel>
                                    <InputGroup>
                                        <UnHighlightedInput
                                            style={{ backgroundColor: "#f7e7bc" }}
                                            value={flightData.stopsMin || 0}
                                            readOnly
                                        />
                                    </InputGroup>
                                </FormGroup>
                            </Col>
                            <Col lg="2">
                                <FormGroup>
                                    <GreyLabel>Stops Max</GreyLabel>
                                    <InputGroup>
                                        <UnHighlightedInput
                                            style={{ backgroundColor: "#f7e7bc" }}
                                            value={flightData.stopsMax || 0}
                                            readOnly
                                        />
                                    </InputGroup>
                                </FormGroup>
                            </Col>
                        </Row>
                    </Collapse>
                </Col>
                {!props.client && (
                    <Col lg="1" style={{ paddingTop: "28px", paddingLeft: "0px" }}>
                        <PinkButtonContainer onClick={() => props.deleteFlight && props.deleteFlight(props.index)}>
                            <EditIcon icon={['fas', 'trash']} style={{ height: "25px" }} />
                        </PinkButtonContainer>
                    </Col>
                )}
            </Row>
        </>
    );
};

export default FlightExperimentalInputRow;

// Styled components
export const ButtonContainer = styled.div`
    margin: 0px 0px;
    width: auto;
    height: 25px;
    border-radius: 25px;
    padding: 15px 5px 5px 5px;
    display: inline;
    &:hover {
        background-color: ${Colors.primaryLight};
    }
`;

export const CollapseIcon = styled(FontAwesomeIcon)`
    margin-right: 0px;
    margin-left: 0px;
    margin-bottom: 4px;
    width: 30px !important;
    height: 15px;
    color: ${Colors.primaryDark};
`;

export const HighlightedInput = styled(Input)`
    font-size: 16px;
    border: none;
    border-bottom: 1px solid ${Colors.primaryLight};
    border-radius: 0px;
    background-color: ${Colors.primaryLight};
    color: ${Colors.primaryDark};
    padding: 5px 5px;
    &:hover {
        border-bottom: 1px solid ${Colors.primaryAccent};
    }
`;

export const UnHighlightedInput = styled(Input)`
    font-size: 16px;
    border: none;
    border-bottom: none;
    border-radius: 0px;
    background-color: ${Colors.primaryLight};
    color: ${Colors.primaryDark};
    padding: 5px 5px;
`;

export const ProviderBadge = styled.span`
    display: inline-block;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: ${props => props.provider === 'flightapi' ? '#007bff' : '#28a745'};
    color: white;
    font-size: 10px;
    font-weight: bold;
    text-align: center;
    line-height: 20px;
    margin-left: 5px;
    position: relative;
    top: -2px;
`;